/**
 * [INPUT]: 无运行时依赖，数值与 Laper-app/shared/ui/motion 的稳定档位同源
 * [OUTPUT]: 对外提供 DUR/EASE/EASE_CSS/SPRING/TRANSITION/VARIANTS/CHART_MOTION 动画令牌
 * [POS]: zinnercms-design-system/motion 的参数真相源，Framer Motion、dither canvas 与 CSS 镜像都从此取语义档
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */

export const DUR = Object.freeze({
  xfast: 0.12,
  fast: 0.15,
  quick: 0.2,
  base: 0.24,
  gentle: 0.32,
  slow: 0.42,
  grand: 0.55,
  chart: 0.68,
})

export const EASE = Object.freeze({
  settle: [0.22, 0.8, 0.36, 1],
  drawer: [0.32, 0.72, 0, 1],
  entrance: [0.2, 0.7, 0.2, 1],
  glide: [0.16, 1, 0.3, 1],
  layoutEase: [0.22, 1, 0.36, 1],
  snap: [0.2, 0, 0, 1],
  exitHard: [0.4, 0, 1, 1],
  out: 'easeOut',
})

const toCssBezier = (curve) => `cubic-bezier(${curve.join(', ')})`

export const EASE_CSS = Object.freeze({
  settle: toCssBezier(EASE.settle),
  drawer: toCssBezier(EASE.drawer),
  entrance: toCssBezier(EASE.entrance),
  glide: toCssBezier(EASE.glide),
  layoutEase: toCssBezier(EASE.layoutEase),
  snap: toCssBezier(EASE.snap),
  exitHard: toCssBezier(EASE.exitHard),
})

// 与 App 同源的禁回弹档位：ζ >= 1，只允许果断趋近，不允许终点过冲。
export const SPRING = Object.freeze({
  tap: { type: 'spring', stiffness: 400, damping: 40 },
  panel: { type: 'spring', stiffness: 420, damping: 35, mass: 0.7 },
  layoutSpring: { type: 'spring', stiffness: 300, damping: 35 },
  drawerSpring: { type: 'spring', stiffness: 280, damping: 34 },
  pop: { type: 'spring', stiffness: 110, damping: 20, mass: 0.9 },
})

export const TRANSITION = Object.freeze({
  pageUnfurl: { duration: DUR.grand, ease: EASE.glide },
  exit: { duration: DUR.fast, ease: EASE.out },
  menu: { duration: DUR.fast },
  modal: { duration: DUR.gentle, ease: EASE.drawer },
  fade: { duration: DUR.quick, ease: EASE.out },
  chartReveal: { duration: DUR.quick, ease: EASE.out },
  meter: { duration: DUR.slow, ease: EASE.glide },
  meterReduced: { duration: DUR.base, ease: EASE.out },
  layoutShift: SPRING.layoutSpring,
})

export const VARIANTS = Object.freeze({
  pagePanel: {
    initial: {
      clipPath: 'inset(0 100% 0 0)',
      filter: 'drop-shadow(16px 0 12px var(--zncms-page-fold-shadow))',
      opacity: 0.94,
    },
    animate: {
      clipPath: 'inset(0 0% 0 0)',
      filter: 'drop-shadow(0 0 0 var(--zncms-page-fold-clear))',
      opacity: 1,
    },
  },
  chart: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
  },
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
})

// dither canvas 使用毫秒；正常档覆盖 PagePanel 画卷时窗，reduced 档直接呈现结果。
export const CHART_MOTION = Object.freeze({
  active: Object.freeze({
    animate: true,
    animationDuration: DUR.chart * 1000,
    bloom: 'low',
    bloomOnHover: true,
  }),
  reduced: Object.freeze({
    animate: false,
    animationDuration: 0,
    bloom: 'off',
    bloomOnHover: false,
  }),
})
